import type { TikTokPage } from '@remotion/captions';
import { getCappedCaptionDisplayLeadMs } from '@/shared/lib/captions/caption-presentation-timing';
import { PAGE_TAIL_PADDING_MS } from './calculate-subtitle-end-frame';

export type CaptionPageSyncLeadInput = {
  pageStartMs: number;
  previousPageEndMs: number;
  maxSyncLeadMs: number;
};

export type CalculateCaptionPageTimingFramesInput = {
  page: TikTokPage;
  nextPage: TikTokPage | null;
  fps: number;
  captionOffsetInSeconds: number;
  previousPageEndMs: number;
  maxSyncLeadMs: number;
  displayEndMs?: number;
  pageTailPaddingMs?: number;
};

export type CaptionPageTimingFrames = {
  startFrame: number;
  endFrame: number;
  durationInFrames: number;
  syncLeadMs: number;
};

const safeMs = (value: number): number =>
  Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;

const pageRawEndMs = (page: TikTokPage): number =>
  safeMs(page.startMs + page.durationMs);

const msToFrame = (
  ms: number,
  fps: number,
  captionOffsetInSeconds: number,
): number => (ms / 1000) * fps - captionOffsetInSeconds * fps;

export const getCappedCaptionPageSyncLeadMs = ({
  pageStartMs,
  previousPageEndMs,
  maxSyncLeadMs,
}: CaptionPageSyncLeadInput): number =>
  getCappedCaptionDisplayLeadMs({
    startMs: pageStartMs,
    previousEndMs: previousPageEndMs,
    maxDisplayLeadMs: maxSyncLeadMs,
  });

export const calculateCaptionPageTimingFrames = ({
  page,
  nextPage,
  fps,
  captionOffsetInSeconds,
  previousPageEndMs,
  maxSyncLeadMs,
  displayEndMs,
  pageTailPaddingMs = PAGE_TAIL_PADDING_MS,
}: CalculateCaptionPageTimingFramesInput): CaptionPageTimingFrames => {
  const syncLeadMs = getCappedCaptionPageSyncLeadMs({
    pageStartMs: page.startMs,
    previousPageEndMs,
    maxSyncLeadMs,
  });
  const startMs = safeMs(page.startMs) - syncLeadMs;
  const startFrame = msToFrame(startMs, fps, captionOffsetInSeconds);

  const pageEndMs = safeMs(page.startMs + page.durationMs + pageTailPaddingMs);
  const previousPageEndForNextPage = Math.max(
    safeMs(previousPageEndMs),
    pageRawEndMs(page),
  );
  const nextActiveStartMs = nextPage
    ? safeMs(nextPage.startMs) -
      getCappedCaptionPageSyncLeadMs({
        pageStartMs: nextPage.startMs,
        previousPageEndMs: previousPageEndForNextPage,
        maxSyncLeadMs,
      })
    : null;
  const explicitDisplayEndMs =
    typeof displayEndMs === 'number' && Number.isFinite(displayEndMs)
      ? Math.max(pageRawEndMs(page), safeMs(displayEndMs))
      : null;
  const endMs =
    nextActiveStartMs == null
      ? (explicitDisplayEndMs ?? pageEndMs)
      : Math.min(pageEndMs, nextActiveStartMs);
  const endFrame = msToFrame(endMs, fps, captionOffsetInSeconds);

  return {
    startFrame,
    endFrame,
    durationInFrames: endFrame - startFrame,
    syncLeadMs,
  };
};
